import {atom, createStore, useAtomValue} from 'jotai';

import {EnvConfig} from '@web-catalog/types/env';

type Store = ReturnType<typeof createStore>;
let _store: Store | null = null;

const isClient = typeof window !== 'undefined';

const secretKeys = [
  'AUTH_SERVER_ID',
  'SERVER_SIDE_URL',
  'SERVER_APP_SELECTION_URL',
  'SERVER_FEEDBACKS_URL',
  'SERVER_STRAPI_URL',
  'SERVER_OMICRON_URL',
  'YANDEX_CAPTCHA_SERVER_ID',
  'HIDDEN_APP_SELECTION_ID',
  'IMAGE_PROXY_KEY',
  'IMAGE_PROXY_SALT',
  'IMAGE_PROXY_URL',
] as const;

export const maskSecrets = (config: EnvConfig) => {
  const copy = {...config};

  for (const key of secretKeys) {
    copy[key] = '*';
  }

  return copy;
};

export const emptyEnvConfig: EnvConfig = {
  ANALYTICS_ID: '',
  API_URL: '',
  COOKIE_DOMAIN: '',
  DEEPLINK_URL: '',
  DEEPLINK_BASE: '',
  GOOGLE_ANALYTICS_ID: '',
  INSTRUCTION_LINK: '',
  LOW_SECRET: '',
  NODE_ENV: '',
  OMICRON_APP_ID: '',
  OMICRON_URL: '',
  SERVER_OMICRON_URL: '',
  SERVER_SIDE_URL: '',
  SERVER_FEEDBACKS_URL: '',
  SERVER_STRAPI_URL: '',
  STAGE: '',
  STRAPI_DATA_CACHE_TIME_MS: 0,
  WEB_APP_VERSION: '',
  YA_METRIKA_ID: '',
  YANDEX_ADS_BANNER_ID: '',
  YANDEX_ADS_FLOOR_AD_MOBILE_ID: '',
  YANDEX_ADS_FLOOR_AD_DESKTOP_ID: '',
  YANDEX_ADS_FLOOR_AD_ALT_MOBILE_ID: '',
  YANDEX_ADS_FLOOR_AD_ALT_DESKTOP_ID: '',
  YANDEX_ADS_FLOOR_AD_BANNER_ID: '',
  YANDEX_ADS_APPS_LIST_BANNER_ID: '',
  YANDEX_ADS_LEFT_SIDEBAR_BANNER_ID: '',
  YANDEX_ADS_RIGHT_SIDEBAR_BANNER_ID: '',
  AUTH_STATE: '',
  AUTH_UUID: '',
  AUTH_SERVER_ID: '',
  VK_ADS_SLOT_ID: '',
  YANDEX_CAPTCHA_CLIENT_ID: '',
  YANDEX_CAPTCHA_SERVER_ID: '',
  SERVER_APP_SELECTION_URL: '',
  HIDDEN_APP_SELECTION_ID: '',
  ALLOW_BYPASS_CAPTCHA: '',
  IMAGE_PROXY_KEY: '',
  IMAGE_PROXY_SALT: '',
  IMAGE_PROXY_URL: '',
  SSP_URL: '',
  SSP_SCRIPT_URL: '',
};

export const envConfigAtom = atom<EnvConfig>(emptyEnvConfig);

export const useEnvConfig = () => {
  // atom contains subset of available keys
  // for server-side rendering full set is returned though
  const atomValue = useAtomValue(envConfigAtom);

  if (isClient) {
    return atomValue;
  }

  return ssrEnvConfig;
};

export const registerStoreForEnvVariablesAccess = (store: Store) => {
  if (isClient) {
    _store = store;
  }
};

const ssrEnvConfig: EnvConfig = {
  ANALYTICS_ID: process.env.ANALYTICS_ID ?? '',
  API_URL: process.env.API_URL ?? '',
  COOKIE_DOMAIN: process.env.COOKIE_DOMAIN ?? '',
  DEEPLINK_URL: process.env.DEEPLINK_URL ?? '',
  DEEPLINK_BASE: process.env.DEEPLINK_BASE ?? 'https://apps.rustore.ru',
  GOOGLE_ANALYTICS_ID: process.env.ANALYTICS_GOOGLE_ID ?? '',
  INSTRUCTION_LINK: process.env.INSTRUCTION_LINK ?? '',
  LOW_SECRET: process.env.LOW_SECRET ?? '',
  NODE_ENV: process.env.NODE_ENV ?? '',
  OMICRON_APP_ID: process.env.OMICRON_APP_ID ?? '',
  OMICRON_URL: process.env.OMICRON_URL ?? '',
  SERVER_OMICRON_URL: process.env.SERVER_OMICRON_URL ?? '',
  SERVER_SIDE_URL: process.env.SERVER_SIDE_URL ?? '',
  SERVER_FEEDBACKS_URL: process.env.SERVER_FEEDBACKS_URL ?? '',
  SERVER_STRAPI_URL: process.env.SERVER_STRAPI_URL ?? '',
  STAGE: process.env.STAGE ?? '',
  STRAPI_DATA_CACHE_TIME_MS: parseInt(
    process.env.STRAPI_DATA_CACHE_TIME_MS ?? '0',
  ),
  WEB_APP_VERSION: process.env.WEB_APP_VERSION ?? '',
  YA_METRIKA_ID: process.env.ANALYTICS_YA_METRIKA_ID ?? '',
  YANDEX_ADS_BANNER_ID: process.env.YANDEX_ADS_BANNER_ID ?? '',
  YANDEX_ADS_FLOOR_AD_MOBILE_ID:
    process.env.YANDEX_ADS_FLOOR_AD_MOBILE_ID ?? '',
  YANDEX_ADS_FLOOR_AD_DESKTOP_ID:
    process.env.YANDEX_ADS_FLOOR_AD_DESKTOP_ID ?? '',
  YANDEX_ADS_FLOOR_AD_ALT_MOBILE_ID:
    process.env.YANDEX_ADS_FLOOR_AD_ALT_MOBILE_ID ??
    process.env.YANDEX_ADS_FLOOR_AD_MOBILE_ID ??
    '',
  YANDEX_ADS_FLOOR_AD_ALT_DESKTOP_ID:
    process.env.YANDEX_ADS_FLOOR_AD_ALT_DESKTOP_ID ??
    process.env.YANDEX_ADS_FLOOR_AD_DESKTOP_ID ??
    '',
  YANDEX_ADS_FLOOR_AD_BANNER_ID:
    process.env.YANDEX_ADS_FLOOR_AD_BANNER_ID ??
    process.env.YANDEX_ADS_BANNER_ID ??
    '',
  YANDEX_ADS_APPS_LIST_BANNER_ID:
    process.env.YANDEX_ADS_APPS_LIST_BANNER_ID ??
    process.env.YANDEX_ADS_BANNER_ID ??
    '',
  YANDEX_ADS_LEFT_SIDEBAR_BANNER_ID:
    process.env.YANDEX_ADS_LEFT_SIDEBAR_BANNER_ID ??
    process.env.YANDEX_ADS_BANNER_ID ??
    '',
  YANDEX_ADS_RIGHT_SIDEBAR_BANNER_ID:
    process.env.YANDEX_ADS_RIGHT_SIDEBAR_BANNER_ID ??
    process.env.YANDEX_ADS_BANNER_ID ??
    '',
  AUTH_STATE: process.env.AUTH_STATE ?? '',
  AUTH_UUID: process.env.AUTH_UUID ?? '',
  AUTH_SERVER_ID: process.env.AUTH_SERVER_ID ?? '',
  VK_ADS_SLOT_ID: process.env.VK_ADS_SLOT_ID ?? '',
  YANDEX_CAPTCHA_CLIENT_ID: process.env.YANDEX_CAPTCHA_CLIENT_ID ?? '',
  YANDEX_CAPTCHA_SERVER_ID: process.env.YANDEX_CAPTCHA_SERVER_ID ?? '',
  SERVER_APP_SELECTION_URL: process.env.SERVER_APP_SELECTION_URL ?? '',
  HIDDEN_APP_SELECTION_ID: process.env.HIDDEN_APP_SELECTION_ID ?? '',
  ALLOW_BYPASS_CAPTCHA: process.env.ALLOW_BYPASS_CAPTCHA ?? '',
  IMAGE_PROXY_KEY: process.env.IMAGE_PROXY_KEY ?? '',
  IMAGE_PROXY_SALT: process.env.IMAGE_PROXY_SALT ?? '',
  IMAGE_PROXY_URL: process.env.IMAGE_PROXY_URL ?? '',
  SSP_URL: process.env.SSP_URL ?? 'https://cheater.dev13.rustore.devmail.ru/v1',
  SSP_SCRIPT_URL:
    process.env.SSP_SCRIPT_URL ??
    'https://ssp-script.dev.rustore.devmail.ru/ssp-script/bundle.js',
};

export const getEnvConfigServerSide = (): EnvConfig => {
  if (isClient && !process.env.STORYBOOK) {
    throw new Error('getEnvConfigServerSide is available only server-side!');
  }

  return ssrEnvConfig;
};

export const getEnvConfigClientSide = (): EnvConfig => {
  if (!isClient) {
    throw new Error('getEnvConfigClientSide is available only client-side!');
  }

  if (!_store) {
    throw new Error(
      'getEnvConfigClientSide: no store was provided, was registerStoreForEnvVariablesAccess called?',
    );
  }

  return _store.get(envConfigAtom);
};

export const getEnvConfig = (): EnvConfig => {
  if (process.env.NODE_ENV === 'test') {
    return ssrEnvConfig;
  }

  if (!isClient) {
    return getEnvConfigServerSide();
  }

  return getEnvConfigClientSide();
};
